import { SearchConditionBase } from '@/common/api/search'
import {
  ListFollowTableHeaderProps,
  makeRectPropsForTableHeadItem,
  useListScrollFollow
} from '@/common/app/list'
import { ActionTabKey } from '@/common/app/modules/media/mediaAction'
import { MediaAction } from '@/common/model/media/mediaAction'

import CheckBox from '@/components/view/atoms/checkbox/CheckBox'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

import MediaActionListRow from '@/components/container/modules/media/automationReview/MediaActionListRow'
import ScrollFollowTable from '@/components/container/utils/list/ScrollFollowTable'

interface TheadProps extends ListFollowTableHeaderProps {
  tab: ActionTabKey
  allSelected: boolean
  someSelected: boolean
  onCheckAll(checked: boolean): void
  float?: boolean
}

const Thead = ({
  tab,
  allSelected,
  someSelected,
  onCheckAll,
  float = false,
  rowRefs,
  rowRects
}: TheadProps) => {
  const rectProps = (index: number) =>
    makeRectPropsForTableHeadItem(rowRefs, rowRects, index, float)

  return (
    <>
      <TableHeadItem checkbox float={float} {...rectProps(0)}>
        {tab === 'suggested' && (
          <CheckBox
            name='select-all-actions'
            checked={allSelected}
            incomplete={someSelected && !allSelected}
            onChange={onCheckAll}
          />
        )}
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(1)}>
        <Text size={12} color={TextColor.SECONDARY}>
          ID
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(2)}>
        <Text size={12} color={TextColor.SECONDARY}>
          優先度
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(3)}>
        <Text size={12} color={TextColor.SECONDARY}>
          アクション種別
          <br />
          /推奨元分析
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(4)}>
        <Text size={12} color={TextColor.SECONDARY}>
          アクション内容
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(5)}>
        <Text size={12} color={TextColor.SECONDARY}>
          関連キーワード
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(6)}>
        <Text size={12} color={TextColor.SECONDARY}>
          ステータス
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(7)}>
        <Text size={12} color={TextColor.SECONDARY}>
          提案日
        </Text>
      </TableHeadItem>
      <TableHeadItem float={float} {...rectProps(8)}>
        <Text size={12} color={TextColor.SECONDARY}>
          操作
        </Text>
      </TableHeadItem>
    </>
  )
}

const MEDIA_ACTION_TABLE_COLUMN_LENGTH = 10

interface Props {
  siteId: number
  items: MediaAction[]
  tab: ActionTabKey
  selectedIds: number[]
  onToggleSelect(id: number): void
  onSelectAll(ids: number[]): void
  onUpdateStatus(id: number, status: string, extra?: { rejection_note?: string }): void
  onChanged(): void
  condition: SearchConditionBase
  onUpdatePage(page: number): void
  total: number
}

// 推奨アクション一覧のテーブル
const MediaActionListTable = ({
  siteId,
  items,
  tab,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onUpdateStatus,
  onChanged,
  condition,
  onUpdatePage,
  total
}: Props) => {
  const allSelected = items.length > 0 && items.every((a) => selectedIds.includes(a.id))

  const listFollowProps = useListScrollFollow(MEDIA_ACTION_TABLE_COLUMN_LENGTH)
  const { header } = listFollowProps

  const theadProps: TheadProps = {
    tab,
    allSelected,
    someSelected: selectedIds.length > 0,
    onCheckAll: () => onSelectAll(allSelected ? [] : items.map((a) => a.id)),
    ...header
  }

  return (
    <ScrollFollowTable
      listFollowProps={listFollowProps}
      tableHead={<Thead {...theadProps} />}
      tableFloatHead={<Thead {...theadProps} float />}
      body={
        <>
          {items.map((a) => (
            <MediaActionListRow
              key={a.id}
              siteId={siteId}
              action={a}
              tab={tab}
              selected={selectedIds.includes(a.id)}
              onToggleSelect={() => onToggleSelect(a.id)}
              onUpdateStatus={onUpdateStatus}
              onChanged={onChanged}
            />
          ))}
        </>
      }
      condition={condition}
      onUpdatePage={onUpdatePage}
      total={total}
    />
  )
}

export default MediaActionListTable
